<template>
  <div v-if="banner && banner.image_url" class="w-full mb-6">
    <div class="py-2 border-b border-gray-200 bg-yellow-50 mb-4">
      <p class="text-xs font-sans text-gray-700 text-center">
        <span class="font-header text-[1.5rem] font-bold">🔴 Live Preview:</span> Banner rendering within admin
        dashboard content area
      </p>
    </div>
    <div :class="bannerWrapperClass" :style="backgroundStyle">
      <div :class="containerClass" :style="containerStyle">
        <div v-if="hasTextOverlay" class="flex flex-col md:flex-row items-center w-full min-w-0" :style="bannerStyle">
          <div v-if="banner.text_position === 'right'" class="flex-1 p-4 md:p-8 flex flex-col justify-center min-w-0"
            :class="textOrderClass" :style="contentStyle">
            <div v-if="banner.text_subheading" :class="[
              banner.text_font || 'font-marketing',
              banner.text_size_class || 'text-xl md:text-2xl',
              'mb-3 text-left'
            ]" :style="{ color: banner.text_color || '#111111' }" v-html="banner.text_subheading">
            </div>
            <div v-if="banner.text_overlay" :class="[
              banner.text_font || 'font-marketing',
              banner.text_size_class ? `${banner.text_size_class.replace('text-', 'text-sm ')} leading-relaxed` : 'text-base md:text-lg leading-relaxed',
              'text-left break-words overflow-hidden'
            ]"
              :style="{ color: banner.text_color || '#111111', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }"
              v-html="banner.text_overlay">
            </div>
          </div>
          <div
            class="relative overflow-hidden border-gray-200 flex-shrink md:flex-shrink-0 flex items-center justify-center"
            :class="[imageBorderClass, imageOrderClass]" :style="imageStyle">
            <a v-if="banner.link_url" :href="banner.link_url"
              class="flex items-center justify-center w-full h-full min-w-0">
              <img :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
                class="w-full h-full" />
            </a>
            <img v-else :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
              class="w-full h-full" />
          </div>
          <div v-if="banner.text_position === 'left'" class="flex-1 p-4 md:p-8 flex flex-col justify-center min-w-0"
            :class="textOrderClass" :style="contentStyle">
            <div v-if="banner.text_subheading" :class="[
              banner.text_font || 'font-marketing',
              banner.text_size_class || 'text-xl md:text-2xl',
              'mb-3 text-left'
            ]" :style="{ color: banner.text_color || '#111111' }" v-html="banner.text_subheading">
            </div>
            <div v-if="banner.text_overlay" :class="[
              banner.text_font || 'font-marketing',
              banner.text_size_class ? `${banner.text_size_class.replace('text-', 'text-sm ')} leading-relaxed` : 'text-base md:text-lg leading-relaxed',
              'text-left break-words overflow-hidden'
            ]"
              :style="{ color: banner.text_color || '#111111', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }"
              v-html="banner.text_overlay">
            </div>
          </div>
        </div>
        <div v-else class="flex justify-center items-center w-full" :style="bannerStyle">
          <div class="relative overflow-hidden w-full max-w-full flex items-center justify-center" :style="imageStyle">
            <a v-if="banner.link_url" :href="banner.link_url"
              class="flex items-center justify-center w-full h-full min-w-0">
              <img :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
                class="w-full h-full" />
            </a>
            <img v-else :src="banner.image_url" :alt="banner.title || 'Banner'" :style="imageCropStyle"
              class="w-full h-full" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  banner: {
    type: Object,
    required: true
  }
})

const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 800)
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1280)

const updateViewportSize = () => {
  if (typeof window !== 'undefined') {
    viewportHeight.value = window.innerHeight
    viewportWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    viewportHeight.value = window.innerHeight
    viewportWidth.value = window.innerWidth
    window.addEventListener('resize', updateViewportSize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateViewportSize)
  }
})

const bannerWrapperClass = computed(() => {
  const baseClasses = 'w-full shadow-lg py-6 md:py-8 border-t border-b border-gray-200 overflow-hidden'
  if (props.banner.position === 'top') {
    return `${baseClasses} mb-6 md:mb-8`
  }
  return baseClasses
})

const containerClass = computed(() => {
  const baseClasses = 'pt-4 md:pt-6 pb-4 md:pb-6 overflow-hidden'
  return `max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${baseClasses}`
})

const containerStyle = computed(() => {
  const style = {
    overflow: 'hidden'
  }
  if (props.banner.max_height) {
    const maxHeight = Math.min(props.banner.max_height, viewportHeight.value * 0.8)
    style.maxHeight = `${maxHeight}px`
  } else {
    style.maxHeight = `${viewportHeight.value * 0.8}px`
  }
  return style
})

const bannerStyle = computed(() => {
  if (props.banner.max_height) {
    const maxHeight = Math.min(props.banner.max_height, viewportHeight.value * 0.8)
    return {
      maxHeight: `${maxHeight}px`,
      overflow: 'hidden'
    }
  }
  return {
    maxHeight: `${viewportHeight.value * 0.8}px`,
    overflow: 'hidden'
  }
})

const hasTextOverlay = computed(() => {
  return !!(props.banner.text_overlay || props.banner.text_subheading)
})

const imageBorderClass = computed(() => {
  if (!hasTextOverlay.value) return ''
  return props.banner.text_position === 'right' ? 'border-l' : 'border-r'
})

const imageStyle = computed(() => {
  const hasText = hasTextOverlay.value

  if (!hasText) {
    return {
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }

  return {
    flex: '0 0 50%',
    maxWidth: '50%',
    width: '50%',
    minWidth: 0,
    minHeight: '300px',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box'
  }
})

const imageCropStyle = computed(() => {
  const style = {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    objectPosition: 'center center',
    display: 'block'
  }

  // If we have image dimensions, use them to calculate the aspect ratio
  const dims = props.banner?.image_crop_dimensions
  if (dims && dims.width > 0 && dims.height > 0) {
    style.aspectRatio = `${dims.width} / ${dims.height}`
  }

  return style
})

const backgroundStyle = computed(() => {
  const style = {
    boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  }
  if (props.banner.text_background) {
    style.backgroundColor = props.banner.text_background
  }
  return style
})

const contentStyle = computed(() => {
  return {
    flex: '1 1 50%',
    maxWidth: '50%',
    boxSizing: 'border-box'
  }
})

const textOrderClass = computed(() => {
  const mobileOrder = props.banner.mobile_order || (props.banner.text_position === 'right' ? 'text-first' : 'image-first')
  const isTextFirstMobile = mobileOrder === 'text-first'
  const isTextLeftDesktop = props.banner.text_position === 'right'

  if (isTextFirstMobile && isTextLeftDesktop) {
    return 'order-1 md:order-1'
  } else if (isTextFirstMobile && !isTextLeftDesktop) {
    return 'order-1 md:order-2'
  } else if (!isTextFirstMobile && isTextLeftDesktop) {
    return 'order-2 md:order-1'
  } else {
    return 'order-2 md:order-2'
  }
})

const imageOrderClass = computed(() => {
  const mobileOrder = props.banner.mobile_order || (props.banner.text_position === 'right' ? 'text-first' : 'image-first')
  const isTextFirstMobile = mobileOrder === 'text-first'
  const isTextLeftDesktop = props.banner.text_position === 'right'

  if (isTextFirstMobile && isTextLeftDesktop) {
    return 'order-2 md:order-2'
  } else if (isTextFirstMobile && !isTextLeftDesktop) {
    return 'order-2 md:order-1'
  } else if (!isTextFirstMobile && isTextLeftDesktop) {
    return 'order-1 md:order-2'
  } else {
    return 'order-1 md:order-1'
  }
})

</script>
<style scoped>
/* Ensure image container respects borders and doesn't overflow */
.relative.overflow-hidden {
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.relative.overflow-hidden img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  margin: auto;
}

.relative.overflow-hidden a {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ensure flex items respect borders */
.flex-row>* {
  box-sizing: border-box;
}

/* Account for border width in flex calculations */
.border-l,
.border-r {
  border-width: 1px;
}
</style>